/**
 * The directory's URL contract: the browser query string is the single source of
 * truth for the public Raises directory's filters + sort, so a filtered view is
 * shareable, survives reload, and is restored by browser Back and the profile's
 * "Back to directory" link.
 *
 *  - {@link parseDirectorySearch} — URL → {@link DirectoryQuery}. Untrusted input:
 *    every value is validated against the canonical vocabularies and anything
 *    unknown or malformed is dropped (never forwarded to the API as a 400).
 *  - {@link serializeDirectoryQuery} — {@link DirectoryQuery} → URL, omitting
 *    defaults, in a stable key order.
 *  - {@link directoryResultSetSignature} — the ONE identity of a result set
 *    (filters + sort, no page window) shared by the query hook's placeholder
 *    guard and the page's infinite-scroll accumulator.
 *  - {@link directoryBackHref} — the profile's validated way back to the view the
 *    viewer came from.
 *
 * URL parameter names are the API's own (`region`, `country`, `sector`, …) with
 * multi-values as CSV, so the page URL reads exactly like the request it drives.
 */
import { REGION_VALUES, type Region } from '@/schemas/region';
import type {
  CanonicalSector,
  CanonicalStage,
  DirectoryQuery,
  DirectorySortKey,
  SortOrder,
} from '@/types/raises';
import { SECTOR_LABELS } from './sectorLabel';
import { STAGE_LABELS } from './stageLabel';

export const DEFAULT_SORT: DirectorySortKey = 'amountStillNeeded';
export const DEFAULT_ORDER: SortOrder = 'desc';

/** Mirrors the backend `MAX_SEARCH_LENGTH` (longer searches 400). */
export const MAX_SEARCH_LENGTH = 200;
/** Mirrors the backend `MAX_COUNTRY_FILTERS`. */
const MAX_COUNTRY_FILTERS = 60;
const COUNTRY_CODE = /^[A-Z]{2}$/;

/** Runtime canonical vocabularies (the label maps are the exhaustive, ordered source). */
const SECTOR_VALUES = Object.keys(SECTOR_LABELS) as CanonicalSector[];
const STAGE_VALUES = Object.keys(STAGE_LABELS) as CanonicalStage[];
const SORT_KEYS: readonly DirectorySortKey[] = [
  'amountStillNeeded',
  'amountSought',
  'amountRaised',
  'name',
  'recency',
];

/** The URL-owned keys, in the fixed order they serialize. */
const URL_KEYS = [
  'q',
  'region',
  'country',
  'sector',
  'stage',
  'minSought',
  'maxSought',
  'minGap',
  'maxGap',
  'hasContact',
  'sort',
  'order',
] as const satisfies ReadonlyArray<keyof DirectoryQuery>;

/** All values of a (possibly repeated) CSV param, trimmed, blanks dropped. */
function listParam(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .flatMap((raw) => raw.split(','))
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/** The deduplicated members of `allowed` present in a CSV param, or undefined. */
function enumList<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
): T[] | undefined {
  const picked = [
    ...new Set(listParam(params, key).filter((v): v is T => allowed.includes(v as T))),
  ];
  return picked.length ? picked : undefined;
}

/** A finite, non-negative number param, or undefined. */
function amountParam(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key)?.trim();
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

/** Drops an inverted (min > max) range — the API rejects it, and intent is unknowable. */
function validRange(min?: number, max?: number): [number | undefined, number | undefined] {
  return min !== undefined && max !== undefined && min > max ? [undefined, undefined] : [min, max];
}

/**
 * Parses the directory URL into a fully-validated query. Sort + order are always
 * present (defaults filled); every filter is present only when valid.
 */
export function parseDirectorySearch(params: URLSearchParams): DirectoryQuery {
  const query: DirectoryQuery = {};

  const q = params.get('q')?.trim().slice(0, MAX_SEARCH_LENGTH).trim();
  if (q) query.q = q;

  const region = enumList<Region>(params, 'region', REGION_VALUES);
  if (region) query.region = region;

  const country = [
    ...new Set(
      listParam(params, 'country')
        .map((code) => code.toUpperCase())
        .filter((code) => COUNTRY_CODE.test(code)),
    ),
  ].slice(0, MAX_COUNTRY_FILTERS);
  if (country.length) query.country = country;

  const sector = enumList(params, 'sector', SECTOR_VALUES);
  if (sector) query.sector = sector;
  const stage = enumList(params, 'stage', STAGE_VALUES);
  if (stage) query.stage = stage;

  const [minSought, maxSought] = validRange(
    amountParam(params, 'minSought'),
    amountParam(params, 'maxSought'),
  );
  const [minGap, maxGap] = validRange(amountParam(params, 'minGap'), amountParam(params, 'maxGap'));
  if (minSought !== undefined) query.minSought = minSought;
  if (maxSought !== undefined) query.maxSought = maxSought;
  if (minGap !== undefined) query.minGap = minGap;
  if (maxGap !== undefined) query.maxGap = maxGap;

  if (params.get('hasContact') === 'true') query.hasContact = true;

  const sort = params.get('sort') as DirectorySortKey | null;
  query.sort = sort && SORT_KEYS.includes(sort) ? sort : DEFAULT_SORT;
  const order = params.get('order');
  query.order = order === 'asc' || order === 'desc' ? order : DEFAULT_ORDER;

  return query;
}

/**
 * Serializes the URL-owned part of a query (filters + sort; never pagination or
 * `includeFacets`) in a stable key order, omitting empty values and the default
 * sort / order.
 */
export function serializeDirectoryQuery(query: DirectoryQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of URL_KEYS) {
    const value = query[key];
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (key === 'sort' && value === DEFAULT_SORT) continue;
    if (key === 'order' && value === DEFAULT_ORDER) continue;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','));
      continue;
    }
    params.set(key, String(value));
  }
  return params;
}

/**
 * The identity of one directory result set: its filters + sort, excluding the
 * page window and `includeFacets`. Equal signatures ⇔ the same rows in the same
 * order, so pages of one signature may be accumulated / shown as placeholders.
 */
export function directoryResultSetSignature(query: DirectoryQuery): string {
  return serializeDirectoryQuery(query).toString();
}

const DIRECTORY_PATH = '/raises/directory';

/**
 * The profile's "Back to directory" target. The directory hands its search string
 * through router `location.state`; history state is page-controlled input, so
 * only a string query (`?…`) is honoured — anything else falls back to the bare
 * directory rather than letting state steer navigation elsewhere.
 */
export function directoryBackHref(state: unknown): string {
  const search =
    state !== null && typeof state === 'object'
      ? (state as { directorySearch?: unknown }).directorySearch
      : undefined;
  return typeof search === 'string' && search.startsWith('?')
    ? `${DIRECTORY_PATH}${search}`
    : DIRECTORY_PATH;
}
